'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import type { CategoryBreakdown } from '@/types/usaspending'

import { Currency, Percentage } from '../Formatters'

export interface AwardMixDonutProps {
  data: CategoryBreakdown[]
  /** Fixed size in pixels, or omit for responsive sizing */
  size?: number
  innerRadiusRatio?: number
  showLegend?: boolean
  className?: string
  onCategoryClick?: (category: string | null) => void
}

// Federal Data Observatory palette - refined, professional
const CATEGORY_COLORS = {
  contract: { bg: 'bg-blue-500', stroke: '#3b82f6', dark: '#60a5fa' },
  idv: { bg: 'bg-indigo-500', stroke: '#6366f1', dark: '#818cf8' },
  grant: { bg: 'bg-purple-500', stroke: '#a855f7', dark: '#c084fc' },
  loans: { bg: 'bg-teal-500', stroke: '#14b8a6', dark: '#2dd4bf' },
  'direct payment': { bg: 'bg-amber-500', stroke: '#f59e0b', dark: '#fbbf24' },
  insurance: { bg: 'bg-rose-500', stroke: '#f43f5e', dark: '#fb7185' },
  other: { bg: 'bg-slate-400', stroke: '#94a3b8', dark: '#cbd5e1' },
} as const

type CategoryKey = keyof typeof CATEGORY_COLORS

const getColorForCategory = (
  category: string | null,
): (typeof CATEGORY_COLORS)[CategoryKey] => {
  const key = (category?.toLowerCase() || 'other') as CategoryKey
  return CATEGORY_COLORS[key] || CATEGORY_COLORS.other
}

/**
 * AwardMixDonut
 *
 * SVG donut chart for award category breakdown.
 * Shows distribution across contract, grant, IDV, loan, etc.
 */
export const AwardMixDonut: React.FC<AwardMixDonutProps> = ({
  data,
  size: fixedSize,
  innerRadiusRatio = 0.6,
  showLegend = true,
  className,
  onCategoryClick,
}) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [measuredSize, setMeasuredSize] = useState(160)

  // Track if we're in mobile layout (stacked)
  const [isStacked, setIsStacked] = useState(false)

  // Responsive sizing - measure container when no fixed size provided
  useEffect(() => {
    if (fixedSize) return

    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const { width, height } = entry.contentRect
        // Stack legend below on small screens (< 400px width)
        const stacked = width < 400
        setIsStacked(stacked)

        // Reserve space for legend (~160px) and gap (24px) when legend is shown horizontally
        const legendSpace = showLegend && !stacked ? 184 : 0
        const availableWidth = width - legendSpace
        // When stacked, use less height since legend is below
        const availableHeight = stacked ? height * 0.6 : height
        // Use the smaller of available width and height to keep chart square
        const maxFromDimensions = Math.min(availableWidth, availableHeight)
        // Clamp to reasonable bounds
        const newSize = Math.max(120, Math.min(280, maxFromDimensions))
        setMeasuredSize(newSize)
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [fixedSize, showLegend])

  const size = fixedSize ?? measuredSize

  // Chart geometry
  const center = size / 2
  const outerRadius = (size - 4) / 2
  const innerRadius = outerRadius * innerRadiusRatio
  const strokeWidth = outerRadius - innerRadius

  // Calculate segments
  const segments = useMemo(() => {
    if (data.length === 0) return []

    const total = data.reduce((sum, d) => sum + d.amount, 0)
    let currentAngle = -90 // Start from top

    return data
      .filter((d) => d.amount > 0) // Only include positive amounts
      .map((d, i) => {
        const percentage = total > 0 ? (d.amount / total) * 100 : 0
        const angle = (percentage / 100) * 360
        const startAngle = currentAngle
        const endAngle = currentAngle + angle
        currentAngle = endAngle

        const color = getColorForCategory(d.category)

        return {
          ...d,
          startAngle,
          endAngle,
          color,
          percentage,
          index: i,
        }
      })
  }, [data])

  // Total amount for center display
  const totalAmount = useMemo(
    () => data.reduce((sum, d) => sum + d.amount, 0),
    [data],
  )

  const totalAwards = useMemo(
    () => data.reduce((sum, d) => sum + d.awardCount, 0),
    [data],
  )

  // Helper to convert polar to cartesian
  const polarToCartesian = (
    cx: number,
    cy: number,
    radius: number,
    angleInDegrees: number,
  ) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    }
  }

  // Create arc path for a segment
  const describeArc = (
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
  ): string => {
    const start = polarToCartesian(cx, cy, radius, endAngle)
    const end = polarToCartesian(cx, cy, radius, startAngle)
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`
  }

  // Get the hovered segment's data
  const hoveredData = hoveredSegment !== null ? segments[hoveredSegment] : null

  const isResponsive = !fixedSize

  if (data.length === 0) {
    return (
      <div
        ref={containerRef}
        className={twMerge(
          'flex items-center justify-center rounded-lg border border-dashed border-primary-300 dark:border-primary-700',
          isResponsive && 'h-full min-h-[200px] flex-1',
          className,
        )}
        style={fixedSize ? { height: fixedSize } : undefined}
      >
        <span className="text-sm text-primary-400 dark:text-primary-500">
          No data available
        </span>
      </div>
    )
  }

  // Calculate center content size to fit inside inner radius with padding
  const centerSize = innerRadius * 2 - 16

  return (
    <div
      ref={containerRef}
      className={twMerge(
        'flex items-center justify-center gap-3 overflow-hidden sm:gap-6',
        isStacked && 'flex-col gap-2',
        isResponsive && 'h-full min-h-[160px] flex-1 sm:min-h-[200px]',
        className,
      )}
    >
      {/* Donut Chart */}
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="overflow-visible">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={(outerRadius + innerRadius) / 2}
            fill="none"
            strokeWidth={strokeWidth}
            className="stroke-primary-100 dark:stroke-primary-800"
          />

          {/* Segments */}
          {segments.map((segment, i) => {
            const radius = (outerRadius + innerRadius) / 2
            const isHovered = hoveredSegment === i

            // Skip tiny segments (less than 0.5%)
            if (segment.percentage < 0.5) return null

            // Handle full circle case (100% or near 100%)
            const isFullCircle = segment.percentage >= 99.5

            return (
              <g
                key={i}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredSegment(i)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => onCategoryClick?.(segment.category)}
              >
                {isFullCircle ? (
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    strokeWidth={strokeWidth + (isHovered ? 2 : 0)}
                    stroke={segment.color.stroke}
                    className="transition-all duration-150"
                  />
                ) : (
                  <path
                    d={describeArc(
                      center,
                      center,
                      radius,
                      segment.startAngle,
                      segment.endAngle,
                    )}
                    fill="none"
                    strokeWidth={strokeWidth + (isHovered ? 2 : 0)}
                    stroke={segment.color.stroke}
                    strokeLinecap="butt"
                    className="transition-all duration-150"
                  />
                )}
              </g>
            )
          })}
        </svg>

        {/* Center content - sized to fit inside inner radius */}
        <div
          className="absolute flex flex-col items-center justify-center text-center pointer-events-none"
          style={{
            width: centerSize,
            height: centerSize,
            left: (size - centerSize) / 2,
            top: (size - centerSize) / 2,
          }}
        >
          {hoveredData ? (
            <>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400 truncate max-w-full">
                {hoveredData.category || 'Other'}
              </span>
              <span className="text-base font-bold tabular-nums text-primary-900 dark:text-primary-100">
                <Percentage value={hoveredData.percentage} />
              </span>
              <span className="text-[10px] tabular-nums text-primary-500 dark:text-primary-400">
                <Currency value={hoveredData.amount} compact />
              </span>
            </>
          ) : (
            <>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400">
                Total
              </span>
              <span className="text-base font-bold tabular-nums text-primary-900 dark:text-primary-100">
                <Currency value={totalAmount} compact />
              </span>
              <span className="text-[10px] text-primary-500 dark:text-primary-400">
                {totalAwards.toLocaleString()} awards
              </span>
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div
          className={twMerge(
            'flex gap-1',
            isStacked ? 'flex-row flex-wrap justify-center' : 'flex-col',
          )}
        >
          {segments
            .filter((s) => s.percentage >= 0.5)
            .map((segment, i) => (
              <button
                key={i}
                className={twMerge(
                  'flex items-center gap-1.5 rounded px-2 py-0.5 text-left transition-all duration-150 sm:gap-2',
                  'hover:bg-primary-100 dark:hover:bg-primary-800',
                  hoveredSegment === segment.index &&
                    'bg-primary-100 dark:bg-primary-800',
                  // Mobile stacked: compact chip style
                  isStacked &&
                    'border border-primary-100 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-800/30',
                )}
                onMouseEnter={() => setHoveredSegment(segment.index)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => onCategoryClick?.(segment.category)}
              >
                <span
                  className="h-2 w-2 rounded-sm shrink-0 sm:h-2.5 sm:w-2.5"
                  style={{ backgroundColor: segment.color.stroke }}
                />
                <span className="text-[10px] font-medium capitalize text-primary-700 sm:text-[11px] dark:text-primary-300">
                  {segment.category || 'Other'}
                </span>
                <span className="text-[10px] tabular-nums text-primary-500 sm:ml-auto sm:text-[11px] dark:text-primary-400">
                  <Percentage value={segment.percentage} />
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}
